import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getMyProfile,
  listMyFanGroups,
  listMyTopupOrders,
  startYoutubeLink,
  unlinkYoutube,
  type FanProfile,
  type FanGender,
  type FanGroupRow,
  type MyTopupOrder,
} from '../api/fan-api';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { signOutFan } from '../auth/fan-auth';
import { EditProfileSheet } from '../components/EditProfileSheet';

const GENDER_LABEL: Record<FanGender, string> = {
  male: '男',
  female: '女',
  other: '其他',
  'prefer-not-to-say': '不願透露',
};

function ProfileDlSkeleton() {
  // Same four-row layout as the loaded view so the grid doesn't reflow.
  const rows: Array<{ labelWidth: string; valueWidth: string }> = [
    { labelWidth: 'w-16', valueWidth: 'w-32' },
    { labelWidth: 'w-12', valueWidth: 'w-48' },
    { labelWidth: 'w-12', valueWidth: 'w-12' },
    { labelWidth: 'w-20', valueWidth: 'w-40' },
  ];
  return (
    <div aria-hidden="true" className="grid grid-cols-3 gap-y-3 text-sm">
      {rows.map((r, i) => (
        <div key={i} className="contents">
          <div className={`h-4 ${r.labelWidth} rounded nx-skeleton-shimmer`} />
          <div className={`col-span-2 h-4 ${r.valueWidth} rounded nx-skeleton-shimmer`} />
        </div>
      ))}
    </div>
  );
}

function GroupRowSkeleton() {
  return (
    <li
      aria-hidden="true"
      className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 space-y-1.5"
    >
      <div className="h-4 w-32 rounded nx-skeleton-shimmer" />
      <div className="h-3 w-48 rounded nx-skeleton-shimmer" />
      <div className="h-3 w-24 rounded nx-skeleton-shimmer" />
    </li>
  );
}

export function Profile() {
  useDocumentTitle('個人資料');
  const navigate = useNavigate();
  const [profile, setProfile] = useState<FanProfile | null>(null);
  const [groups, setGroups] = useState<FanGroupRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [groupsErr, setGroupsErr] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const out = await getMyProfile();
        if (alive) setProfile(out);
      } catch (e) {
        if (alive) setErr((e as Error).message);
      }
    })();
    (async () => {
      try {
        const out = await listMyFanGroups();
        if (alive) setGroups(out.items ?? []);
      } catch (e) {
        if (alive) setGroupsErr((e as Error).message);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="max-w-2xl mx-auto px-3 sm:px-4 py-4 sm:py-10 space-y-6 sm:space-y-8">
      <section>
        <div className="mb-4 sm:mb-6 flex items-center justify-between gap-2">
          <h1 className="text-xl sm:text-2xl font-semibold">個人資料</h1>
          {profile && (
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="rounded-full border border-slate-700 bg-slate-900 hover:bg-slate-800 text-slate-200 text-sm font-medium px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-violet-500"
              aria-label="編輯個人資料"
            >
              編輯
            </button>
          )}
        </div>
        {err && <div role="alert" className="text-red-400 text-sm">{err}</div>}
        {!err && !profile && (
          <div aria-live="polite" aria-busy="true">
            <ProfileDlSkeleton />
          </div>
        )}
        {profile && (
          <dl className="grid grid-cols-3 gap-y-3 text-sm">
            <dt className="text-slate-500">顯示名稱</dt>
            <dd className="col-span-2">{profile.displayName || <span className="text-slate-600">（未設定）</span>}</dd>
            <dt className="text-slate-500">Email</dt>
            <dd className="col-span-2 break-all">{profile.email}</dd>
            {/* Birthday + gender added in feat 3a39592 (post-signup
                setup modal). Previously hidden from the fan after they
                submitted them — making the data invisible is hostile,
                even if the modal is one-time. Show with "（未設定）"
                fallbacks so legacy rows without these fields render
                cleanly. */}
            <dt className="text-slate-500">生日</dt>
            <dd className="col-span-2">{profile.birthday || <span className="text-slate-600">（未設定）</span>}</dd>
            <dt className="text-slate-500">性別</dt>
            <dd className="col-span-2">{profile.gender ? GENDER_LABEL[profile.gender] : <span className="text-slate-600">（未設定）</span>}</dd>
            <dt className="text-slate-500">語系</dt>
            <dd className="col-span-2">{profile.locale}</dd>
            <dt className="text-slate-500">加入時間</dt>
            <dd className="col-span-2">{new Date(profile.createdAt).toLocaleString()}</dd>
          </dl>
        )}
      </section>

      <section>
        <h2 className="text-lg font-medium mb-3">我的群組</h2>
        {groupsErr && <div role="alert" className="text-red-400 text-sm">{groupsErr}</div>}
        {!groupsErr && groups === null && (
          <ul className="space-y-2" aria-live="polite" aria-busy="true">
            <GroupRowSkeleton />
            <GroupRowSkeleton />
          </ul>
        )}
        {groups && groups.length === 0 && (
          <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/50 p-3 text-slate-500 text-sm">
            你還沒有加入任何群組。等 IP 經營者邀請你加入後就會出現在這裡。
          </div>
        )}
        {groups && groups.length > 0 && (
          <ul className="space-y-2">
            {groups.map((g) => (
              <li key={g.groupId} className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2">
                <div className="font-medium">{g.name}</div>
                {g.description && <div className="text-slate-400 text-xs mt-0.5">{g.description}</div>}
                <div className="text-slate-500 text-[11px] mt-1">
                  加入時間：{new Date(g.joinedAt).toLocaleDateString()}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 儲值紀錄 — pop-up listing the fan's own topup orders
          (時間 / 金額 / 點數 / 狀態). */}
      <TopupHistoryCard />

      {/* Per `fan-youtube-integration` — link YouTube card. Linked
          state shows channel title + thumbnail; unlinked shows the
          connect CTA. Linking redirects via Google OAuth (full page)
          and returns to /fan/profile?yt=ok|err.
          HIDDEN while the Google OAuth sensitive-scope review is
          pending (YouTube scopes removed from the consent screen
          2026-07-10 → the link flow would fail with invalid_scope).
          Kept visible for already-linked fans so they can still see /
          unlink their channel. Re-enable with
          VITE_YT_INTEGRATION_ENABLED=true. */}
      {(import.meta.env?.VITE_YT_INTEGRATION_ENABLED === 'true' || profile?.linkedYouTube) && (
      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 space-y-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-sm font-semibold text-slate-100">YouTube 連結</h2>
          <span className="text-[11px] text-slate-500">選填</span>
        </div>
        {profile?.linkedYouTube ? (
          <div className="flex items-center gap-3">
            <img
              src={profile.linkedYouTube.thumbnailUrl}
              alt={profile.linkedYouTube.channelTitle}
              className="h-10 w-10 rounded-full bg-slate-800"
            />
            <div className="flex-1 min-w-0">
              <div className="truncate text-sm text-slate-100">{profile.linkedYouTube.channelTitle}</div>
              <div className="text-[11px] text-slate-500 font-mono truncate">{profile.linkedYouTube.channelId}</div>
            </div>
            <button
              type="button"
              onClick={async () => {
                if (!confirm('要解除 YouTube 連結？')) return;
                try {
                  await unlinkYoutube();
                  const fresh = await getMyProfile();
                  setProfile(fresh);
                } catch (e) { alert((e as Error).message); }
              }}
              className="rounded-lg border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-200 px-3 py-1.5 text-xs"
            >
              解除連結
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-slate-400">
              連結 YouTube 後可以用你的頻道暱稱顯示，並在你訂閱會員的角色頁看到會員徽章。
            </p>
            <button
              type="button"
              onClick={async () => {
                try {
                  const r = await startYoutubeLink();
                  window.location.href = r.authUrl;
                } catch (e) { alert((e as Error).message); }
              }}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 hover:border-violet-500/40 px-4 py-2 text-sm text-slate-100"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="#FF0000" aria-hidden="true">
                <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.4.5A3 3 0 0 0 .5 6.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.1c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.1c.5-1.9.5-5.8.5-5.8s0-3.9-.5-5.8zM9.6 15.6V8.4l6.3 3.6-6.3 3.6z" />
              </svg>
              連結 YouTube 帳號
            </button>
          </div>
        )}
      </section>
      )}

      {/* Account-level actions. Sign-out duplicates the AppHeader's
          logout button, but on mobile the AppHeader scrolls out of
          view as the fan reads down; putting it here means the
          standard "settings page sign-out" pattern (Apple, Google,
          every social app) just works. Bigger tap target (py-3)
          than the header's pill. */}
      <section className="pt-2">
        <button
          type="button"
          onClick={() => { signOutFan(); navigate('/'); }}
          className="w-full sm:w-auto rounded-lg border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-200 px-4 py-3 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-rose-400"
        >
          登出
        </button>
      </section>

      {/* Edit sheet — reuses FanProfileForm so the field set stays
          in sync with the first-login modal. */}
      {profile && (
        <EditProfileSheet
          open={editOpen}
          current={profile}
          onClose={() => setEditOpen(false)}
          onSaved={(next) => setProfile(next)}
        />
      )}
    </div>
  );
}

// ── 儲值紀錄 ─────────────────────────────────────────────────────
const TOPUP_STATUS_META: Record<string, { label: string; cls: string }> = {
  success: { label: '成功', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  pending: { label: '處理中', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  failed: { label: '失敗', cls: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
};

function TopupHistoryCard() {
  const [open, setOpen] = useState(false);
  const [orders, setOrders] = useState<MyTopupOrder[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const openModal = async (): Promise<void> => {
    setOpen(true);
    setErr(null);
    try { setOrders(await listMyTopupOrders()); }
    catch (e) { setErr((e as Error).message || '載入失敗'); }
  };

  return (
    <>
      <section className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-slate-100">儲值紀錄</h2>
            <p className="text-xs text-slate-400 mt-0.5">查看你每一筆儲值的時間、金額與狀態。</p>
          </div>
          <button
            type="button"
            onClick={() => void openModal()}
            className="shrink-0 rounded-lg border border-slate-700 bg-slate-900 hover:border-violet-500/40 hover:bg-slate-800 px-4 py-2 text-sm text-slate-100 transition"
          >
            查看紀錄
          </button>
        </div>
      </section>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-lg max-h-[80vh] overflow-hidden rounded-2xl border border-slate-800 bg-slate-900 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-800 px-5 py-3">
              <h3 className="text-base font-semibold text-slate-100">儲值紀錄</h3>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-200">✕</button>
            </div>
            <div className="overflow-y-auto">
              {err && <div className="m-4 rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-300">{err}</div>}
              {!err && orders === null && <div className="p-6 text-center text-sm text-slate-500">載入中…</div>}
              {!err && orders !== null && orders.length === 0 && (
                <div className="p-8 text-center text-sm text-slate-500">還沒有任何儲值紀錄。</div>
              )}
              {!err && orders !== null && orders.length > 0 && (
                <table className="w-full text-left text-sm">
                  <thead className="border-b border-slate-800 text-[11px] uppercase tracking-wider text-slate-500 sticky top-0 bg-slate-900">
                    <tr>
                      <th className="px-4 py-2.5 font-medium">時間</th>
                      <th className="px-4 py-2.5 text-right font-medium">金額</th>
                      <th className="px-4 py-2.5 text-right font-medium">點數</th>
                      <th className="px-4 py-2.5 font-medium">狀態</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/70">
                    {orders.map((o) => {
                      const meta = TOPUP_STATUS_META[o.status] ?? { label: o.status, cls: 'bg-slate-600/20 text-slate-300 border-slate-600/40' };
                      return (
                        <tr key={o.tradeNo} className="text-slate-200">
                          <td className="px-4 py-2.5 text-slate-300">
                            {o.createdAt ? new Date(o.createdAt).toLocaleString('zh-TW', { hour12: false }) : '—'}
                            <div className="text-[10px] font-mono text-slate-600">{o.tradeNo}</div>
                          </td>
                          <td className="px-4 py-2.5 text-right tabular-nums">NT$ {o.amountTwd.toLocaleString()}</td>
                          <td className="px-4 py-2.5 text-right tabular-nums text-violet-300">+{o.pointsCredited.toLocaleString()}</td>
                          <td className="px-4 py-2.5">
                            <span className={`inline-block rounded-full border px-2 py-0.5 text-xs font-medium ${meta.cls}`}>{meta.label}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
